import { z } from "zod";
import {
  AgentId,
  ApprovalId,
  ConversationId,
  CredentialId,
  Locale,
  Ok,
  PageQuery,
  ProjectId,
  SessionId,
  TemplateId,
  TenantId,
  TerminalId,
  page,
} from "./common.js";
import {
  Agent,
  AgentStatus,
  Approval,
  ApprovalDecision,
  Conversation,
  Credential,
  FsEntry,
  FsPath,
  Membership,
  Message,
  Part,
  Project,
  Template,
  Tenant,
  TenantRole,
  Theme,
  User,
} from "./entities.js";

/**
 * REST 路由表 —— 客户端 REST 的唯一定义（doc/api_v1.md §1）。
 * 控制面据此注册路由与校验；client-sdk、OpenAPI、msw mock 均由它生成。
 * 范围：P1 所需。后续阶段在对应阶段开工前由集成者追加（契约冻结）。
 */

import { r, type RouteDef } from "./route-def.js";
import { collabRoutes } from "./rest-collab.js";
import { capabilityRoutes } from "./rest-capability.js";
export type { Permission, RouteDef } from "./route-def.js";

const TidParams = z.object({ tid: TenantId });
const PidParams = z.object({ pid: ProjectId });
const AidParams = z.object({ aid: AgentId });

/** Web cookie 模式：会话 cookie + double-submit CSRF（前端读 CSRF cookie，写进 CSRF 头） */
export const SESSION_COOKIE = "as_session";
export const CSRF_COOKIE = "as_csrf";
export const CSRF_HEADER = "x-csrf-token";

export const Session = z.object({ user: User, accessToken: z.string().optional(), refreshToken: z.string().optional() });

export const coreRoutes = {
  // ───────── auth ─────────
  authRegister: r({
    method: "POST", path: "/auth/register", summary: "注册", tags: ["auth"], auth: "public",
    body: z.object({ email: z.email(), password: z.string().min(8).max(128), displayName: z.string().min(1).max(64), locale: Locale, mode: z.enum(["cookie", "token"]).default("cookie"), inviteCode: z.string().max(128).optional() }),
    response: Session, errors: ["auth.email_taken", "auth.registration_closed"],
  }),
  authLogin: r({
    method: "POST", path: "/auth/login", summary: "登录（web 下发 cookie；mode=token 时返回 bearer）", tags: ["auth"], auth: "public",
    body: z.object({ email: z.email(), password: z.string(), mode: z.enum(["cookie", "token"]).default("cookie") }),
    response: Session, errors: ["auth.invalid_credentials"],
  }),
  authRefresh: r({
    method: "POST", path: "/auth/refresh", summary: "刷新：token 模式带 refreshToken 轮换；cookie 模式不带 body，续期会话 cookie", tags: ["auth"], auth: "public",
    body: z.object({ refreshToken: z.string().optional() }), response: Session, errors: ["auth.refresh_invalid"],
  }),
  authLogout: r({ method: "POST", path: "/auth/logout", summary: "登出", tags: ["auth"], auth: "user", response: Ok }),
  wsTicket: r({
    method: "POST", path: "/auth/ws-ticket", summary: "换取一次性 WS 票据", tags: ["auth"], auth: "user",
    response: z.object({ ticket: z.string(), expiresInSec: z.number().int() }),
  }),
  meGet: r({
    method: "GET", path: "/me", summary: "当前用户（isPlatformAdmin：是否平台管理员，决定是否显示管理控制台）", tags: ["me"], auth: "user",
    response: User.extend({ isPlatformAdmin: z.boolean().optional() }),
  }),
  mePatch: r({
    method: "PATCH", path: "/me", summary: "更新个人设置", tags: ["me"], auth: "user",
    body: z.object({ displayName: z.string().min(1).max(64), locale: Locale, timezone: z.string(), theme: Theme }).partial(),
    response: User,
  }),

  // ───────── tenants ─────────
  tenantList: r({
    method: "GET", path: "/tenants", summary: "我所在的租户", tags: ["tenant"], auth: "user",
    response: z.object({ items: z.array(Tenant.extend({ role: TenantRole })) }),
  }),
  tenantCreate: r({
    method: "POST", path: "/tenants", summary: "创建租户", tags: ["tenant"], auth: "user", idempotent: true,
    body: Tenant.pick({ name: true, slug: true }), response: Tenant, errors: ["common.conflict"],
  }),
  tenantGet: r({ method: "GET", path: "/tenants/:tid", summary: "租户详情", tags: ["tenant"], auth: "user", permission: "tenant.read", params: TidParams, response: Tenant }),
  memberList: r({
    method: "GET", path: "/tenants/:tid/members", summary: "成员列表", tags: ["tenant"], auth: "user", permission: "tenant.read",
    params: TidParams, response: z.object({ items: z.array(Membership) }),
  }),

  // ───────── credentials ─────────
  credentialList: r({
    method: "GET", path: "/tenants/:tid/credentials", summary: "凭据列表（不含 secret）", tags: ["credential"], auth: "user",
    permission: "tenant.read", params: TidParams, response: z.object({ items: z.array(Credential) }),
  }),
  credentialCreate: r({
    method: "POST", path: "/tenants/:tid/credentials", summary: "添加凭据", tags: ["credential"], auth: "user", idempotent: true,
    permission: "credential.manage", params: TidParams,
    body: z.discriminatedUnion("kind", [
      z.object({ kind: z.literal("subscription"), label: z.string().max(64), token: z.string().min(10) }),
      z.object({ kind: z.literal("platform"), label: z.string().max(64) }),
      z.object({ kind: z.literal("pool"), label: z.string().max(64), memberIds: z.array(CredentialId).min(1).max(50) }),
    ]),
    response: Credential, errors: ["credential.invalid"],
  }),
  credentialUpdate: r({
    method: "PATCH", path: "/credentials/:cid", summary: "修改凭据名称；pool 可调整成员（顺序即优先级）", tags: ["credential"], auth: "user",
    permission: "credential.manage", params: z.object({ cid: CredentialId }),
    body: z.object({ label: z.string().max(64).optional(), memberIds: z.array(CredentialId).min(1).max(50).optional() }),
    response: Credential, errors: ["credential.invalid"],
  }),
  credentialVerify: r({
    method: "POST", path: "/credentials/:cid/verify", summary: "经 llm-gw 探测凭据", tags: ["credential"], auth: "user",
    permission: "credential.manage", params: z.object({ cid: CredentialId }), response: Credential, errors: ["credential.verify_failed"],
  }),
  credentialDelete: r({
    method: "DELETE", path: "/credentials/:cid", summary: "吊销凭据", tags: ["credential"], auth: "user",
    permission: "credential.manage", params: z.object({ cid: CredentialId }), response: Ok, errors: ["credential.in_use"],
  }),

  // ───────── projects ─────────
  projectList: r({
    method: "GET", path: "/tenants/:tid/projects", summary: "项目列表", tags: ["project"], auth: "user", permission: "tenant.read",
    params: TidParams, query: PageQuery, response: page(Project),
  }),
  projectCreate: r({
    method: "POST", path: "/tenants/:tid/projects", summary: "创建项目", tags: ["project"], auth: "user", idempotent: true,
    permission: "project.write", params: TidParams, body: Project.pick({ name: true, description: true, locale: true }), response: Project,
  }),
  projectGet: r({ method: "GET", path: "/projects/:pid", summary: "项目详情", tags: ["project"], auth: "user", permission: "project.read", params: PidParams, response: Project }),
  projectPatch: r({
    method: "PATCH", path: "/projects/:pid", summary: "更新项目", tags: ["project"], auth: "user", permission: "project.write",
    params: PidParams, body: Project.pick({ name: true, description: true, locale: true }).partial(), response: Project,
  }),

  // ───────── templates ─────────
  templateList: r({
    method: "GET", path: "/templates", summary: "身份模板（内置 + 本租户）", tags: ["template"], auth: "user",
    query: z.object({ tenantId: TenantId.optional(), role: z.string().optional() }), response: z.object({ items: z.array(Template) }),
  }),
  templateGet: r({ method: "GET", path: "/templates/:tplid", summary: "模板详情", tags: ["template"], auth: "user", params: z.object({ tplid: TemplateId }), response: Template }),

  // ───────── agents ─────────
  agentList: r({
    method: "GET", path: "/projects/:pid/agents", summary: "项目内 agent", tags: ["agent"], auth: "user", permission: "agent.read",
    params: PidParams, response: z.object({ items: z.array(Agent) }),
  }),
  agentCreate: r({
    method: "POST", path: "/projects/:pid/agents", summary: "创建 agent（异步 provisioning）", tags: ["agent"], auth: "user", idempotent: true,
    permission: "agent.control", params: PidParams,
    body: z.object({
      templateId: TemplateId,
      name: Agent.shape.name,
      credentialId: CredentialId,
      monthlyLimitMicroUsd: z.number().int().nonnegative().nullable().optional(),
    }),
    response: Agent, errors: ["credential.not_allowed", "node.none_available"],
  }),
  agentGet: r({ method: "GET", path: "/agents/:aid", summary: "agent 详情", tags: ["agent"], auth: "user", permission: "agent.read", params: AidParams, response: Agent }),
  agentDelete: r({ method: "DELETE", path: "/agents/:aid", summary: "删除 agent（销毁容器）", tags: ["agent"], auth: "user", permission: "agent.control", params: AidParams, response: Ok }),
  agentAction: r({
    method: "POST", path: "/agents/:aid/actions/:action", summary: "生命周期操作", tags: ["agent"], auth: "user", permission: "agent.control",
    params: AidParams.extend({ action: z.enum(["start", "pause", "resume", "reset", "interrupt"]) }),
    response: z.object({ status: AgentStatus }), errors: ["agent.not_running", "agent.unreachable"],
  }),
  agentInput: r({
    method: "POST", path: "/agents/:aid/input", summary: "用户直接对 agent 说话（写入 DM 会话并唤醒）", tags: ["agent"], auth: "user", idempotent: true,
    permission: "agent.control", params: AidParams, body: z.object({ parts: z.array(Part).min(1) }),
    response: Message, errors: ["agent.budget_exhausted", "agent.unreachable", "agent.not_running"],
  }),
  agentSessions: r({
    method: "GET", path: "/agents/:aid/sessions", summary: "会话列表", tags: ["agent"], auth: "user", permission: "agent.read", params: AidParams,
    response: z.object({ items: z.array(z.object({ id: SessionId, startedAt: z.iso.datetime(), lastActiveAt: z.iso.datetime(), title: z.string().nullable() })) }),
  }),
  agentSessionEvents: r({
    method: "GET", path: "/agents/:aid/sessions/:sid/events", summary: "会话事件回放", tags: ["agent"], auth: "user", permission: "agent.read",
    params: AidParams.extend({ sid: SessionId }), query: PageQuery,
    response: page(z.object({ seq: z.number().int(), type: z.string(), data: z.unknown(), at: z.iso.datetime() })),
  }),

  // ───────── approvals ─────────
  approvalList: r({
    method: "GET", path: "/projects/:pid/approvals", summary: "审批列表", tags: ["approval"], auth: "user", permission: "project.read",
    params: PidParams, query: PageQuery.extend({ status: z.enum(["pending", "allowed", "denied", "expired"]).optional() }), response: page(Approval),
  }),
  approvalDecide: r({
    method: "POST", path: "/approvals/:apid", summary: "审批决定", tags: ["approval"], auth: "user", permission: "approval.decide",
    params: z.object({ apid: ApprovalId }),
    body: z.object({ decision: ApprovalDecision, remember: z.enum(["once", "session", "always"]).default("once"), note: z.string().max(500).optional() }),
    response: Approval, errors: ["approval.already_resolved"],
  }),

  // ───────── fs / terminal ─────────
  fsTree: r({
    method: "GET", path: "/agents/:aid/fs/tree", summary: "目录树", tags: ["fs"], auth: "user", permission: "agent.read", params: AidParams,
    query: z.object({ path: FsPath.default(""), depth: z.coerce.number().int().min(1).max(5).default(1) }), response: z.object({ entries: z.array(FsEntry) }),
    errors: ["fs.path_invalid", "fs.not_found", "agent.unreachable"],
  }),
  fsRead: r({
    method: "GET", path: "/agents/:aid/fs/file", summary: "读文件（响应头带 ETag）", tags: ["fs"], auth: "user", permission: "agent.read", params: AidParams,
    query: z.object({ path: FsPath }),
    response: z.union([
      z.object({ kind: z.literal("inline"), content: z.string(), encoding: z.enum(["utf8", "base64"]), etag: z.string() }),
      z.object({ kind: z.literal("download"), url: z.url(), etag: z.string(), size: z.number().int() }),
    ]),
    errors: ["fs.not_found", "fs.is_directory", "agent.unreachable"],
  }),
  fsWrite: r({
    method: "PUT", path: "/agents/:aid/fs/file", summary: "写文件（If-Match 乐观锁）", tags: ["fs"], auth: "user", permission: "agent.fs.write", params: AidParams,
    query: z.object({ path: FsPath }), body: z.object({ content: z.string(), encoding: z.enum(["utf8", "base64"]).default("utf8") }),
    response: z.object({ etag: z.string() }), errors: ["common.precondition_failed", "fs.too_large", "agent.unreachable"],
  }),
  fsOps: r({
    method: "POST", path: "/agents/:aid/fs/ops", summary: "移动 / 删除 / 建目录", tags: ["fs"], auth: "user", permission: "agent.fs.write", params: AidParams,
    body: z.object({ op: z.enum(["move", "delete", "mkdir"]), path: FsPath, to: FsPath.optional() }), response: Ok,
  }),
  terminalOpen: r({
    method: "POST", path: "/agents/:aid/terminals", summary: "打开终端（之后订阅 terminal:<id>）", tags: ["fs"], auth: "user", permission: "agent.terminal", params: AidParams,
    body: z.object({ cols: z.number().int().positive(), rows: z.number().int().positive() }), response: z.object({ terminalId: TerminalId }),
    errors: ["agent.not_running", "agent.unreachable"],
  }),

  // ───────── conversations（P1 只有 dm；room/meeting 在 P2/P3 扩展） ─────────
  conversationList: r({
    method: "GET", path: "/projects/:pid/conversations", summary: "会话列表", tags: ["conversation"], auth: "user", permission: "project.read",
    params: PidParams, query: PageQuery.extend({ kind: z.enum(["dm", "room", "meeting", "mail_thread"]).optional() }), response: page(Conversation),
  }),
  messageList: r({
    method: "GET", path: "/conversations/:cid/messages", summary: "消息列表（倒序游标）", tags: ["conversation"], auth: "user", permission: "project.read",
    params: z.object({ cid: ConversationId }), query: PageQuery, response: page(Message),
  }),
} as const satisfies Record<string, RouteDef>;

/** 全部路由；控制面按 phase 过滤注册（phase 缺省 = 1） */
export const routes = { ...coreRoutes, ...collabRoutes, ...capabilityRoutes } as const;

export type Routes = typeof routes;
export type RouteName = keyof Routes;
